import { useQuery } from '@tanstack/react-query';
import { getFlatFeeBillingDashboard } from 'services/billingServices';

export const FLAT_FEE_BILLING_DASHBOARD_QUERY_KEY = 'flat-fee-billing-dashboard';
export const CLIENT_FEE_LEDGER_QUERY_KEY = 'client-fee-ledger';

export const invalidateClientFlatFeeBillingQueries = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [FLAT_FEE_BILLING_DASHBOARD_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [CLIENT_FEE_LEDGER_QUERY_KEY] }),
  ]);

const extractListSection = (section, { legacyUnpaidKey, legacyViewAllKey, legacyUnpaidNestedKey, raw }) => {
  if (Array.isArray(section)) {
    return {
      items: section,
      unpaid_count: raw?.[legacyUnpaidKey] ?? raw?.unpaid_count?.[legacyUnpaidNestedKey] ?? 0,
      view_all: raw?.[legacyViewAllKey] ?? raw?.view_all?.[legacyUnpaidNestedKey] ?? false,
    };
  }

  if (section && typeof section === 'object') {
    return {
      items: Array.isArray(section.items) ? section.items : [],
      unpaid_count: section.unpaid_count ?? 0,
      view_all: section.view_all ?? false,
    };
  }

  return { items: [], unpaid_count: 0, view_all: false };
};

const normalizeSummary = (raw) => {
  const summary = raw?.summary;

  if (summary && typeof summary === 'object') {
    return {
      flat_fee_total: summary.flat_fee_total ?? summary.total,
      total: summary.total,
      balance_due: summary.balance_due,
      past_due: summary.past_due,
      current_due: summary.current_due,
      future_due: summary.future_due,
      available_trust_balance: summary.available_trust_balance,
      total_trust_balance: summary.total_trust_balance,
      total_paid: summary.total_paid,
    };
  }

  const balanceDue = raw?.balance_due;

  if (balanceDue && typeof balanceDue === 'object') {
    return {
      flat_fee_total: raw?.flat_fee_total ?? raw?.total,
      total: raw?.total,
      balance_due: balanceDue.total ?? balanceDue.balance_due,
      past_due: balanceDue.past_due,
      current_due: balanceDue.current_due,
      future_due: balanceDue.future_due,
    };
  }

  return {
    flat_fee_total: raw?.flat_fee_total ?? raw?.total,
    total: raw?.total,
    balance_due: balanceDue,
    past_due: raw?.past_due,
    current_due: raw?.current_due,
    future_due: raw?.future_due,
  };
};

const hasDisplayableSummary = (summary) => {
  if (!summary || typeof summary !== 'object') return false;
  return [summary.flat_fee_total, summary.total, summary.balance_due].some(
    (value) => value !== undefined && value !== null && !Number.isNaN(Number(value))
  );
};

export const normalizeFeeLedger = (feeLedger) => {
  if (!feeLedger) return null;

  const pagination = feeLedger.pagination ?? {};

  return {
    ...feeLedger,
    page_size: feeLedger.page_size ?? pagination.page_size ?? 10,
    current_page: feeLedger.current_page ?? pagination.current_page ?? 1,
    total_pages: feeLedger.total_pages ?? pagination.total_pages ?? 1,
    total_records: feeLedger.total_records ?? pagination.total_records ?? 0,
  };
};

export const hasFeeLedgerTransactions = (feeLedger) => {
  const normalized = normalizeFeeLedger(feeLedger);
  if (!normalized) return false;

  const totalRecords = normalized.total_records ?? 0;
  const txCount = Array.isArray(normalized.transactions) ? normalized.transactions.length : 0;

  return totalRecords > 0 || txCount > 0;
};

const resolveSummaryCardsVisible = (sectionsVisible, summary) => {
  if (sectionsVisible?.summary_cards === false) return false;
  if (sectionsVisible?.summary_cards === true) return true;
  return hasDisplayableSummary(summary);
};

export const normalizeFlatFeeDashboardResponse = (raw = {}) => {
  const invoicesSection = extractListSection(raw.invoices, {
    legacyUnpaidKey: 'invoices_unpaid_count',
    legacyViewAllKey: 'invoices_view_all',
    legacyUnpaidNestedKey: 'invoices',
    raw,
  });

  const paymentRequestsSection = extractListSection(raw.payment_requests, {
    legacyUnpaidKey: 'payment_requests_unpaid_count',
    legacyViewAllKey: 'payment_requests_view_all',
    legacyUnpaidNestedKey: 'payment_requests',
    raw,
  });

  const summary = normalizeSummary(raw);
  const sectionsVisible = {
    ...(raw.sections_visible ?? {}),
    summary_cards: resolveSummaryCardsVisible(raw.sections_visible, summary),
  };

  return {
    ...raw,
    case_name: raw.case_name,
    confido_enabled: raw.confido_enabled ?? false,
    sections_visible: sectionsVisible,
    summary,
    payment_requests: paymentRequestsSection.items,
    payment_requests_unpaid_count: paymentRequestsSection.unpaid_count,
    payment_requests_view_all: paymentRequestsSection.view_all,
    invoices: invoicesSection.items,
    invoices_unpaid_count: invoicesSection.unpaid_count,
    invoices_view_all: invoicesSection.view_all,
    fee_ledger: normalizeFeeLedger(raw.fee_ledger),
  };
};

export const useFlatFeeBillingDashboardQuery = ({
  tenant_id,
  tenant_sk,
  case_id,
  contact_id,
  ledger_page = 1,
  ledger_page_size = 10,
  enabled = true,
}) => {
  return useQuery({
    queryKey: [FLAT_FEE_BILLING_DASHBOARD_QUERY_KEY, case_id, contact_id, ledger_page, ledger_page_size],
    queryFn: async () => {
      const response = await getFlatFeeBillingDashboard(tenant_id, tenant_sk, case_id, contact_id, ledger_page, ledger_page_size);
      return normalizeFlatFeeDashboardResponse(response?.data ?? {});
    },
    enabled: Boolean(enabled && tenant_id && tenant_sk && case_id && contact_id),
    suspense: true,
    useErrorBoundary: true,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};
